export const apiBaseUrl = `/api`;
export const apiAssetsUrl = `${apiBaseUrl}/assets`;


export interface HTTPError {
    code: number;
    text: string;
}


export enum ShopGroup {
    Stationary = "Stationary",
    Household = "Household",
    ArtPrints = "Art Prints",
}

interface DbModel {
    _id: string;
}

export interface KofiEvent extends DbModel {
    verification_token: string;
    message_id: string;
    timestamp: string;
    type: string;
    url: string;
    is_public: boolean;
    from_name: string;
    message: string;
    email: string;
    amount: string | number;
    currency: string;
    is_subscription_payment: boolean;
    is_first_subscription_payment: boolean;
    tier_name: string;
    kofi_transaction_id: string;
    discord_username: string;
    discord_userid: string;
}

export interface ShopImage {
    id: string;
    name: string;
    sku: string;
}

export interface ShopItem {
    id: string;
    name: string;
    sku: string;
    price: number;
    stock: number;
    order_limit: number;
    tags: string[];
    description: string;
}

export interface ShopListing extends DbModel {
    name: string;
    description: string;
    group: ShopGroup | null;
    images: ShopImage[];
    items: ShopItem[];
    tags: string[];
    active: boolean;
    offer_active: boolean;
    offer_threshold: number;
    offer_price: number;
}

export interface Voucher extends DbModel {
    code: string;
    comment: string;
    store_credit: number;
    free_shipping: boolean;
}

export interface Order extends DbModel {
    items: ShopItem[];
}

export type CartEntry = {
    id: string;
    quantity: number;
};

export type Cart = {
    entries: CartEntry[];
    vouchers: Voucher[];
};

export type BingoItem = {
    stream_type: string;
    description: string;
};


export type CartChangeCallback = (cart: Cart) => void;


let localId = 0;
let cachedCart: Cart | null = null;
let cartChangeCallbacks: {[id: string]: CartChangeCallback} = {};


function generateLocalId(): string {
    const id = localId.toString();
    localId += 1;
    return id;
}


function dataQueryString(data: {[key: string]: string}): string {
    const components = [];
    for (const [key, value] of Object.entries(data)) {
        components.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
    }
    return "?" + components.join("&");
}


export async function getBingoBoard(username: string, streamType: string): Promise<BingoItem[][]> {
    return await jsonRequest("GET", `/bingo/${streamType}/${username}`);
}


export function addCartChangeCallback(callback: CartChangeCallback): string {
    const id = generateLocalId();
    cartChangeCallbacks[id] = callback;
    try {
        callback(getCart());
    } catch {
        console.error("Error in cartChangeCallback");
    }
    return id;
}


export function removeCartChangeCallback(id: string) {
    delete cartChangeCallbacks[id];
}


function createCart(): Cart {
    return {entries: [], vouchers: []};
}


function updateCart(cart: Cart) {
    cachedCart = cart;
    localStorage.setItem("cart", JSON.stringify(cart));
    for (const callback of Object.values(cartChangeCallbacks)) {
        try {
            callback(cart);
        } catch {
            console.error("Error in cartChangeCallback");
        }
    }
}


export function getCart(): Cart {
    if (cachedCart !== null) {
        return cachedCart;
    }

    const cartJson = localStorage.getItem("cart");
    if (cartJson) {
        cachedCart = JSON.parse(cartJson) as Cart;
        if (!Array.isArray(cachedCart.entries)) {
            cachedCart.entries = [];
        }
        if (!Array.isArray(cachedCart.vouchers)) {
            cachedCart.vouchers = [];
        }
    } else {
        cachedCart = createCart();
    }

    return cachedCart;
}


export function getCartCount(id: string): number {
    const cart = getCart();
    for (const cartEntry of cart.entries) {
        if (cartEntry.id === id) {
            return cartEntry.quantity;
        }
    }
    return 0;
}


export function addToCart(id: string, quantity: number) {
    const cart = getCart();
    let found = false;
    for (const cartEntry of cart.entries) {
        if (cartEntry.id === id) {
            cartEntry.quantity += quantity;
            found = true;
            break;
        }
    }
    if (!found && quantity > 0) {
        cart.entries.push({id: id, quantity: quantity});
    }
    updateCart(cart);
}


export function addVoucherToCart(voucher: Voucher) {
    const cart = getCart();
    let voucherFound = false;
    for (const existingVoucher of cart.vouchers) {
        if (existingVoucher.code === voucher.code) {
            Object.assign(existingVoucher, voucher);
            voucherFound = true;
        }
    }
    if (!voucherFound) {
        cart.vouchers.push(voucher);
    }
    updateCart(cart);
}


export function removeVoucherFromCart(code: string) {
    const cart = getCart();
    let removeIndex = -1;
    for (const [index, voucher] of cart.vouchers.entries()) {
        if (voucher.code === code) {
            removeIndex = index;
            break;
        }
    }
    if (removeIndex !== -1) {
        cart.vouchers.splice(removeIndex, 1);
    }
    updateCart(cart);
}


export function removeFromCart(id: string) {
    const cart = getCart();
    let removeIndex = -1;
    for (const [index, cartEntry] of cart.entries.entries()) {
        if (cartEntry.id === id) {
            removeIndex = index;
            break;
        }
    }
    if (removeIndex !== -1) {
        cart.entries.splice(removeIndex, 1);
    }
    updateCart(cart);
}


export function reduceQuantityInCart(id: string, quantity: number) {
    const cart = getCart();
    let removeIndex = -1;
    for (const [index, cartEntry] of cart.entries.entries()) {
        if (cartEntry.id === id) {
            cartEntry.quantity -= quantity;
            if (cartEntry.quantity <= 0) {
                removeIndex = index;
            }
            break;
        }
    }
    if (removeIndex !== -1) {
        cart.entries.splice(removeIndex, 1);
    }
    updateCart(cart);
}


/**
 * @throws {HTTPError}
 */
export async function jsonRequest(method: string, endpoint: string, body: any = null) {
    const request: RequestInit = {
        method: method,
        headers: {"Content-Type": "application/json"},
    };
    if (body !== null) {
        request.body = JSON.stringify(body);
    }

    const response = await fetch(`${apiBaseUrl}${endpoint}`, request);
    if (response.status != 200) {
        throw {
            code: response.status,
            text: await response.text(),
        }
    }

    return await response.json();
}


export async function uploadFileRequest(endpoint: string, file: any, data: any = null) {
    const formData = new FormData();
    formData.append("file", file, file.name);

    let url = `/api${endpoint}`;
    if (data !== null) {
        url += dataQueryString(data);
    }

    const response = await fetch(url, {
        method: "POST",
        body: formData,
    });

    if (response.status != 200) {
        throw Error(`HTTP ${response.status}: ${await response.text()}`);
    }

    return response.json();
}

export async function getVoucherFromCode(code: string): Promise<Voucher|null> {
    try {
        return await jsonRequest("GET", `/shop/voucher/${code}`);
    } catch {
        return null;
    }
}

export async function getShopListing(itemId: string): Promise<ShopListing> {
    return await jsonRequest("GET", `/shop/item/${itemId}`);
}

export async function getShopListings(): Promise<ShopListing[]> {
    const listings: ShopListing[] = await jsonRequest("GET", "/shop/list");
    listings.sort((a, b) => {return a.name.localeCompare(b.name)});
    return listings;
}

export async function getKofiMembers(): Promise<string[]> {
    return await jsonRequest("GET", "/kofi/members");
}

export async function getPubkey(): Promise<string> {
    const result = await jsonRequest("GET", "/email/public-key");
    return result.pubkey;
}

export async function sendEmail(name: string, email: string, text: string, pubkey: string) {
    const messageBytes = new TextEncoder().encode(pubkey + text);
    let signature = "";
    for (const byte of new Uint8Array(await crypto.subtle.digest('SHA-256', messageBytes))) {
        if (byte < 16) {
            signature += "0" + byte.toString(16);
        } else {
            signature += byte.toString(16);
        }
    }
    return await jsonRequest("POST", "/email/send", {
        email,
        name,
        message: text,
        signature
    });
}
